import React from 'react';
import { motion } from 'framer-motion';
import AdvocacyPhoto from '../../images/Health_WWD.jpg';
import ActionPhoto from "../../images/Peace_WWD.JPG";
import EducationPhoto from "../../images/Hero_WWD.jpg";
import { ArrowRight } from 'lucide-react';

// For the homepage version, we'll import images from your existing assets
// You'll need to update these paths to match your project structure
// const EducationPhoto = "/path-to-education-image.jpg"; // Add an education-related image

const WhatWeDoHome = () => {
  return (
    <section id="what-we-do" className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display text-h2 text-secondary mb-2">WHAT WE DO</h2>
          <p className="text-bodyLarge text-textPrimary max-w-2xl mx-auto mb-4">
            Through Education, Action, and Advocacy, we create lasting impact in refugee communities.
          </p>
          <div className="w-16 h-1 bg-cta mx-auto"></div>
        </div>
        
        {/* Three pillars - desktop view is side by side, mobile is stacked */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Education Pillar */}
          <motion.div 
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full"
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="h-48 overflow-hidden">
              <img 
                src={EducationPhoto} 
                alt="Education initiatives" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
            <div className="p-6 flex-grow">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
              </div>
              <h3 className="font-display text-h3 text-secondary mb-3">Education</h3>
              <p className="text-textPrimary mb-4">
                We deliver lectures, workshops, and tutoring programs that spread awareness about the refugee crisis and provide direct academic support to refugee students.
              </p>
              <div className="mt-auto">
                <div className="text-primary font-bold flex items-center">
                  <span className="text-sm">1,000+ Tutoring Hours</span>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Action Pillar */}
          <motion.div 
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full"
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="h-48 overflow-hidden">
              <img 
                src={ActionPhoto} 
                alt="Direct action initiatives" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
            <div className="p-6 flex-grow">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"></path>
                </svg>
              </div>
              <h3 className="font-display text-h3 text-secondary mb-3">Action</h3>
              <p className="text-textPrimary mb-4">
                We deliver essential goods to refugee families and support community initiatives like the Refugee Peace Farm, creating tangible impact through direct action.
              </p>
              <div className="mt-auto">
                <div className="text-primary font-bold flex items-center">
                  <span className="text-sm">100% Zero-Overhead Donations</span>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Advocacy Pillar */}
          <motion.div 
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full"
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="h-48 overflow-hidden">
              <img 
                src={AdvocacyPhoto} 
                alt="Advocacy initiatives" 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
            <div className="p-6 flex-grow">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"></path>
                </svg>
              </div>
              <h3 className="font-display text-h3 text-secondary mb-3">Advocacy</h3>
              <p className="text-textPrimary mb-4">
                We amplify refugee voices through events like our annual Refugee Health Fair and Mental Health Conference, driving awareness and policy change.
              </p>
              <div className="mt-auto">
                <div className="text-primary font-bold flex items-center">
                  <span className="text-sm">4000+ Refugees Helped</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Featured program/commitment section */}
        <div className="bg-lightTeal rounded-xl p-8 md:p-10">
          <div className="flex flex-col md:flex-row gap-10 items-center">
            <div className="md:w-1/2">
              <h3 className="font-display text-h3 text-secondary mb-4">Our Commitment</h3>
              <p className="text-textPrimary mb-6">
                Students for Refugees stands on three unshakable pillars: Education, Action, and Advocacy.
                Together, these principles guide our work as we strive to better the condition of refugees and,
                more specifically, refugee students.
              </p>
              <a href="/impact" className="inline-flex items-center text-cta font-bold hover:underline">
                Learn more about our work
                <ArrowRight size={16} className="ml-2" />
              </a>
            </div>
            <div className="md:w-1/2 grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg text-center">
                <div className="font-display text-h2 text-cta">1,000+</div>
                <div className="text-textPrimary">Tutoring Hours</div>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <div className="font-display text-h2 text-cta">50+</div>
                <div className="text-textPrimary">Active Tutors</div>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <div className="font-display text-h2 text-cta">4000+</div>
                <div className="text-textPrimary">Refugees Helped</div>
              </div>
              <div className="bg-white p-4 rounded-lg text-center">
                <div className="font-display text-h2 text-cta">3</div>
                <div className="text-textPrimary">Focus Areas</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoHome;